import {Component, inject, OnInit, signal} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {BaseOverlayComponent} from "../../../../shared/components/overlay/base-overlay/base-overlay.component";
import {TextInputComponent} from "../../../../shared/inputs/text-input/text-input.component";
import {DIALOG_DATA, DialogRef} from "@angular/cdk/dialog";
import {TableStateEvent} from "../../../../shared/components/data-table/data-table.component";
import {BaseComponent} from "../../../../shared/base/base-component";
import {State} from "../../../../shared/base/base-state";
import {ApiService} from "../../../../core/api/api.service";
import {ButtonComponent} from "../../../../shared/components/button/button.component";
import {AppSvgIconComponent} from "../../../../shared/components/app-svg-icon/app-svg-icon.component";
import {BaseInputComponent} from "../../../../shared/inputs/_base/base-input/base-input.component";
import {PaginationState} from "../../../../shared/base/base-pagination-state";
import {CheckboxComponent} from "../../../../shared/inputs/checkbox/checkbox.component";
import {
  AdminCreateUserRequest,
  AdminCreateUserResponse,
  AdminUpdateUserRequest,
  AdminUpdateUserResponse,
  GetCompanyListItem,
  GetCompanyListParams,
  GetCompanyListResponse
} from "../../../../core/api/model";

@Component({
  selector: 'app-create-or-update-user',
  imports: [
    BaseOverlayComponent,
    ReactiveFormsModule,
    TextInputComponent,
    ButtonComponent,
    FormsModule,
    AppSvgIconComponent,
    BaseInputComponent,
    CheckboxComponent
  ],
  templateUrl: './create-or-update-user.component.html',
  styleUrl: './create-or-update-user.component.scss'
})
export class CreateOrUpdateUserComponent extends BaseComponent implements OnInit {

  formBuilder = inject(FormBuilder);

  data = inject(DIALOG_DATA);

  dialogRef = inject(DialogRef);

  apiService = inject(ApiService);

  createOrUpdateForm!: FormGroup;

  companyListState = new PaginationState<GetCompanyListResponse>();

  adminCreateUserState = new State<AdminCreateUserResponse>();

  adminUpdateUserState = new State<AdminUpdateUserResponse>();

  selectedCompanyIds = signal<string[]>([]);

  pageNumber = 1;

  pageSize = 1000;

  totalCount = 0;

  isAllSelected = false;

  companyList: any[] = [];

  filteredCompanyList: any[] = [];

  searchText = '';

  ngOnInit() {

    this.initForm();

    this.getCompanyList({
      searchText: '',
      paginationEvent: {
        pageNumber: 1,
        pageSize: this.pageSize
      }
    });

  }

  initForm() {

    this.createOrUpdateForm = this.formBuilder.group({
      id: [null],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      companyIds: [null, Validators.required],
      companies: [[], Validators.required]
    });

    this.updateForm();

  }

  getCompanyList(event?: TableStateEvent) {

    let page = event?.paginationEvent?.pageNumber;

    if (!page || page <= 0) {
      page = 1;
    }

    this.pageNumber = page;

    this.pageSize = event?.paginationEvent?.pageSize ?? this.pageSize;

    let request: GetCompanyListParams = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      search: event?.searchText ?? ''
    };

    this.executeRequest<GetCompanyListResponse>({
      state: this.companyListState,
      request: this.apiService.getCompanyList(request),
      onSuccess: (response: GetCompanyListResponse) => {

        this.totalCount = response.totalCount ?? 0;

        this.companyList = (response.data ?? []).map(company => ({
          ...company,
          isChecked: false
        }));

        this.filteredCompanyList = [...this.companyList];

        if (this.data && !this.createOrUpdateForm.value.companyIds) {

          let companyIds = this.data.companyIds ?? [];

          this.companyList.forEach(company => {
            company.isChecked = companyIds.includes(company.id);
          });

          this.selectedCompanyIds.set(companyIds);

          this.isAllSelected =
            this.companyList.length > 0 &&
            this.companyList.every(company => company.isChecked);

          this.createOrUpdateForm.patchValue({
            companyIds: companyIds,
            companies: companyIds
          });

        }

      }
    });

  }

  updateForm() {

    if (!this.data) {
      return;
    }

    this.createOrUpdateForm.controls["id"].setValue(this.data?.id);

    this.createOrUpdateForm.controls["firstName"].setValue(this.data?.firstName);

    this.createOrUpdateForm.controls["lastName"].setValue(this.data?.lastName);

    this.createOrUpdateForm.controls["email"].setValue(this.data?.email);

    this.createOrUpdateForm.controls["phoneNumber"].setValue(this.data?.phoneNumber);

    if (this.data != null) {
      this.createOrUpdateForm.controls["email"].disable();
    }

  }

  createUser() {

    let value = this.createOrUpdateForm.value;

    let companyIds = (value.companyIds || []).map((c: String) => c);

    if (!Array.isArray(companyIds)) {
      companyIds = [companyIds];
    }

    let formValue = this.createOrUpdateForm.value;

    let request: AdminCreateUserRequest = {
      companyIds: companyIds,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      phoneNumber: formValue.phoneNumber,
      email: formValue.email
    };

    this.executeRequest<AdminCreateUserResponse>({
      state: this.adminCreateUserState,
      request: this.apiService.createAdminUser(request),
      handleSuccess: true,
      onSuccess: () => {
        this.dialogRef.close(true);
      }
    });

  }

  updateUser() {

    let id = this.data.id;

    let value = this.createOrUpdateForm.value;

    let companyIds = (value.companyIds || []).map((c: String) => c);

    if (!Array.isArray(companyIds)) {
      companyIds = [companyIds];
    }

    let formValue = this.createOrUpdateForm.value;

    let updateUserRequest: AdminUpdateUserRequest = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      phoneNumber: formValue.phoneNumber,
      companyIds: companyIds
    };

    this.executeRequest({
      state: this.adminUpdateUserState,
      request: this.apiService.updateAdminUser(id, updateUserRequest),
      handleSuccess: true,
      onSuccess: () => {
        this.dialogRef.close(true);
      }
    });

  }

  onSubmitClicked() {

    let companies = this.selectedCompanyIds();

    if (!companies || companies.length === 0) {

      this.toasterService.error('Please select at least one company');

      return;

    }

    this.createOrUpdateForm.patchValue({
      companies: companies,
      companyIds: companies
    });

    this.validateForm(this.createOrUpdateForm);

    if (this.createOrUpdateForm.invalid) {
      return;
    }

    if (this.data == null) {
      this.createUser();
    } else {
      this.updateUser();
    }

  }

  isSelected(company: GetCompanyListItem): boolean {
    return this.selectedCompanyIds().includes(company.id);
  }

  removeCompany(company: GetCompanyListItem) {

    let updated = this.selectedCompanyIds().filter(id => id !== company.id);

    this.selectedCompanyIds.set(updated);

    this.companyList.forEach(item => {

      if (item.id === company.id) {
        item.isChecked = false;
      }

    });

    this.isAllSelected =
      this.companyList.length > 0 &&
      this.companyList.every(company => company.isChecked);

  }

  onCompanySelected(company: any, isChecked: boolean) {

    company.isChecked = isChecked;

    let currentCompanyIds = this.selectedCompanyIds();

    if (isChecked) {

      if (!currentCompanyIds.includes(company.id)) {

        this.selectedCompanyIds.set([
          ...currentCompanyIds,
          company.id
        ]);

      }

    } else {

      this.selectedCompanyIds.set(
        currentCompanyIds.filter(id => id !== company.id)
      );

    }

    this.isAllSelected =
      this.companyList.length > 0 &&
      this.companyList.every(company => company.isChecked);

    this.createOrUpdateForm.patchValue({
      companies: this.selectedCompanyIds(),
      companyIds: this.selectedCompanyIds()
    });

  }

  onSelectAllCompanies(isChecked: boolean) {

    this.isAllSelected = isChecked;

    this.filteredCompanyList.forEach(company => {
      company.isChecked = isChecked;
    });

    if (isChecked) {

      const allIds = this.filteredCompanyList.map(company => company.id);

      this.selectedCompanyIds.set(allIds);

    } else {

      this.selectedCompanyIds.set([]);

    }

    this.createOrUpdateForm.patchValue({
      companies: this.selectedCompanyIds(),
      companyIds: this.selectedCompanyIds()
    });

  }

  onSearchCompany(event: Event) {

    const value = (event.target as HTMLInputElement).value;

    this.searchText = value;

    if (!value) {

      this.filteredCompanyList = [...this.companyList];

      return;

    }

    this.filteredCompanyList = this.companyList.filter(company =>
      company.companyName.toLowerCase().includes(value.toLowerCase())
    );

  }

}
